import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  Close,
  DeleteOutline,
  DriveFileMoveOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import type { ReactElement, ReactNode } from "react";

import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import { CALIBRATE_COLORS, NAV_SECTION_COLORS } from "components/hoc/Theme";
import useResponsive from "hooks/useResponsive";

import type { BulkActions } from "./selectionModel";
import type { BulkAction } from "./useBulkFileActions";

/** A disabled button says why on hover: MUI drops events of disabled buttons,
 * so the tooltip wraps a span. */
function Why({
  reason,
  children,
}: {
  reason: string | null;
  children: ReactElement;
}) {
  if (!reason) return children;
  return (
    <Tooltip title={reason}>
      <span>{children}</span>
    </Tooltip>
  );
}

const DANGER_SX = {
  color: CALIBRATE_COLORS.dangerText,
  borderColor: CALIBRATE_COLORS.dangerText,
  "&:hover": {
    borderColor: CALIBRATE_COLORS.dangerText,
    bgcolor: CALIBRATE_COLORS.dangerHover,
  },
} as const;

/** Takes the place of the toolbar while files are selected: how many, a way to
 * take the whole list, and what can be done with them. */
export default function SelectionBar({
  label,
  selectAll,
  actions,
  readOnly,
  busy,
  progress,
  onAction,
  onClear,
}: {
  /** "3 files selected". */
  label: string;
  /** "Select all 47 files", when there is more to take. */
  selectAll: ReactNode;
  actions: BulkActions;
  readOnly: boolean;
  busy: boolean;
  /** What a running download is doing. */
  progress: string | null;
  onAction: (action: BulkAction) => void;
  onClear: () => void;
}) {
  const { isPhone } = useResponsive();

  const button = (
    action: BulkAction,
    text: string,
    icon: ReactElement,
    disabled: boolean,
    danger = false,
  ) =>
    isPhone ? (
      <IconButton
        size="small"
        aria-label={text}
        disabled={disabled}
        onClick={() => onAction(action)}
        sx={danger ? { color: CALIBRATE_COLORS.dangerText } : undefined}
      >
        {icon}
      </IconButton>
    ) : (
      <Button
        variant="outlined"
        size="small"
        startIcon={icon}
        disabled={disabled}
        onClick={() => onAction(action)}
        sx={danger ? DANGER_SX : undefined}
      >
        {text}
      </Button>
    );

  // What changes files stays visible but off in Client View.
  const write = (
    action: BulkAction,
    text: string,
    icon: ReactElement,
    reason: string | null,
    danger = false,
  ) =>
    readOnly ? (
      <ReadOnlyTooltip active>
        {button(action, text, icon, true, danger)}
      </ReadOnlyTooltip>
    ) : (
      <Why reason={reason}>
        {button(action, text, icon, busy || Boolean(reason), danger)}
      </Why>
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.25, sm: 1.5 },
        minHeight: 72,
        flexShrink: 0,
        // The Workspace tint of the page's header icon.
        bgcolor: NAV_SECTION_COLORS.workspace.iconBackground,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Check fontSize="small" sx={{ color: "primary.main" }} aria-hidden />
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {label}
        </Typography>
        {selectAll}
        {progress ? (
          <Typography variant="body2" color="text.secondary">
            {progress}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          ml: "auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {actions.addToProject
          ? write(
              "add_to_project",
              "Add to Project",
              <DriveFileMoveOutlined fontSize="small" />,
              actions.addToProject.disabled,
            )
          : null}
        <Why reason={actions.download.disabled}>
          {button(
            "download",
            "Download",
            <FileDownloadOutlined fontSize="small" />,
            busy || Boolean(actions.download.disabled),
          )}
        </Why>
        {write(
          "delete",
          "Delete",
          <DeleteOutline fontSize="small" />,
          actions.delete.disabled,
          true,
        )}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {isPhone ? (
          <IconButton
            size="small"
            aria-label="Clear selection"
            onClick={onClear}
          >
            <Close fontSize="small" />
          </IconButton>
        ) : (
          <Button
            size="small"
            startIcon={<Close fontSize="small" />}
            onClick={onClear}
            aria-label="Clear selection"
            sx={{ color: "text.secondary" }}
          >
            Clear
          </Button>
        )}
      </Box>
    </Box>
  );
}

/** "Select all 47 files" as a link-looking button. */
export function SelectAllLink({
  total,
  busy,
  onClick,
}: {
  total: number;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      disabled={busy}
      onClick={onClick}
      sx={{ fontWeight: 600 }}
    >
      {busy ? "Selecting..." : `Select all ${total} files`}
    </Link>
  );
}
